import { AlertCircle, Check, Loader2 } from 'lucide-react';

export type BulkRowStatus = 'idle' | 'creating' | 'success' | 'failed';

export interface BulkRegisterRowData {
  index: number;
  proxy: string;
  status: BulkRowStatus;
  username?: string;
  password?: string;
  displayName?: string;
  error?: string;
}

const CHIP_SUCCESS =
  'inline-flex items-center gap-1 rounded border border-success-deep bg-success-bg px-2 py-0.5 text-[10px] font-bold text-success';

const CHIP_FAILED =
  'inline-flex items-center gap-1 rounded border border-danger-border bg-danger-bg px-2 py-0.5 text-[10px] font-bold text-danger';

/** Một dòng thông tin trong bảng kết quả. */
function Field({ label, value, valueClass }: { label: string; value: string; valueClass: string }) {
  return (
    <div>
      {label}: <strong className={valueClass}>{value}</strong>
    </div>
  );
}

/**
 * Ô "Kết quả" của một proxy trong bảng đăng ký hàng loạt.
 *
 * Tách khỏi bảng vì bốn trạng thái vẽ ra bốn khối JSX khác hẳn nhau — để chung
 * thì thân component chính dài gấp đôi và không đọc nổi cái nào thuộc trạng thái nào.
 */
function ResultCell({ row }: { row: BulkRegisterRowData }) {
  switch (row.status) {
    case 'idle':
      return <span className="text-ink-dim text-[12px] font-medium">Chờ tạo...</span>;

    case 'creating':
      return (
        <span className="text-accent inline-flex items-center gap-1.5 text-[12px] font-semibold">
          <Loader2 size={13} className="animate-spin" />
          Đang tạo tài khoản...
        </span>
      );

    case 'success':
      return (
        <div className="space-y-1.5">
          <span className={CHIP_SUCCESS}>
            <Check size={10} />
            Thành công
          </span>
          <dl className="text-ink-muted space-y-0.5 font-mono text-[11px]">
            <Field
              label="Tên đăng nhập"
              value={row.username ?? '—'}
              valueClass="text-ink font-bold"
            />
            {/* Mật khẩu tô màu cảnh báo: đây là lần DUY NHẤT nó hiện ra. */}
            <Field
              label="Mật khẩu"
              value={row.password ?? '—'}
              valueClass="text-warning font-bold"
            />
            <Field
              label="Tên hiển thị"
              value={row.displayName || '—'}
              valueClass="text-ink font-bold"
            />
          </dl>
        </div>
      );

    case 'failed':
      return (
        <div className="space-y-1.5">
          <span className={CHIP_FAILED}>
            <AlertCircle size={10} />
            Thất bại
          </span>
          <div className="text-danger text-[12px] font-semibold">{row.error}</div>
        </div>
      );
  }
}

export function BulkRegisterRow({ row }: { row: BulkRegisterRowData }) {
  return (
    <tr className="border-line hover:bg-panel-hover border-t transition-colors">
      <td className="text-ink-dim px-5 py-3 text-center font-mono text-[12px] font-bold">
        {row.index}
      </td>
      <td className="text-accent px-5 py-3 font-mono text-[12px]">{row.proxy}</td>
      <td className="px-5 py-3">
        <ResultCell row={row} />
      </td>
    </tr>
  );
}
